// Device identity and seats (TDD: Frontend, Identity).
//
// The device ID lives in localStorage. A seat (room code + player token) is
// kept in sessionStorage, so each tab is its own player and a refresh rejoins
// the same seat, and in localStorage, so a reopened tab can offer to continue
// as that player instead of silently taking it over.
//
// crypto.randomUUID needs a secure context, which a phone on the LAN over
// plain http doesn't have, so IDs come from crypto.getRandomValues.

const DEVICE_KEY = "dp.device";
const SEAT_PREFIX = "dp.seat.";
const PROFILE_KEY = "dp.profile";

export interface Seat {
  roomCode: string;
  playerId: string;
  playerToken: string;
  nickname: string;
}

export interface Profile {
  nickname: string;
  color: string;
}

export function randomId(bytes = 16): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
}

function read(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function write(storage: Storage, key: string, value: string | null) {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the game still works for this tab.
  }
}

let memoryDevice: string | null = null;

export function deviceId(): string {
  const stored = read(localStorage, DEVICE_KEY);
  if (stored) return stored;
  memoryDevice ??= randomId();
  write(localStorage, DEVICE_KEY, memoryDevice);
  return memoryDevice;
}

function parseSeat(raw: string | null): Seat | null {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Seat;
    return s.playerToken && s.roomCode ? s : null;
  } catch {
    return null;
  }
}

/** The seat this tab holds in a room. */
export function tabSeat(code: string): Seat | null {
  return parseSeat(read(sessionStorage, SEAT_PREFIX + code));
}

/** A seat this device held in a room, possibly from another tab. */
export function deviceSeat(code: string): Seat | null {
  return parseSeat(read(localStorage, SEAT_PREFIX + code));
}

export function saveSeat(seat: Seat) {
  const raw = JSON.stringify(seat);
  write(sessionStorage, SEAT_PREFIX + seat.roomCode, raw);
  write(localStorage, SEAT_PREFIX + seat.roomCode, raw);
}

export function forgetSeat(code: string) {
  write(sessionStorage, SEAT_PREFIX + code, null);
  write(localStorage, SEAT_PREFIX + code, null);
}

export function claimSeat(seat: Seat) {
  write(sessionStorage, SEAT_PREFIX + seat.roomCode, JSON.stringify(seat));
}

export function loadProfile(): Profile | null {
  try {
    const raw = read(localStorage, PROFILE_KEY);
    return raw ? (JSON.parse(raw) as Profile) : null;
  } catch {
    return null;
  }
}

export function saveProfile(p: Profile) {
  write(localStorage, PROFILE_KEY, JSON.stringify(p));
}
